import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import CCUS from '@/pages/CCUS';
import CountryOverviews from '@/pages/CountryOverviews';
import GHG from '@/pages/GHG';
import { GlobalDownloadsPage } from '@/pages/GlobalDownload';
import GuidanceDocumentation from '@/pages/GuidanceDocumentation';
import LandingPage from '@/pages/LandingPage';
import { MaOpportunitiesPage } from '@/pages/MaOpportunities';
import { MaTransactionsPage } from '@/pages/MaTransactions';
import OilAndGasMap from '@/pages/OilAndGasMap';
import PortfolioAnalysis from '@/pages/PortfolioAnalysis';
import Settings from '@/pages/Settings';
import SubscriberDownload from '@/pages/SubscriberDownload';
import Valuations from '@/pages/Valuations';
import AssetTemplate from '@/pages/Valuations/AssetTemplate';
import ValuationsV2 from '@/pages/ValuationsV2';
import { VideoTutorials } from '@/pages/VideoTutorials';
import {
  CCUS_PATH,
  COUNTRY_OVERVIEWS_PATH,
  CUSTOMER_DOWNLOADS_PATH,
  DEFAULT_PATH,
  GHG_PATH,
  GLOBAL_DOWNLOADS_PATH,
  GLOBAL_DOWNLOADS_PATH_WITH_DOWNLOADSPANE,
  M_A_OPPORTUNITIES_PATH,
  M_A_TRANSACTIONS_PATH,
  OIL_AND_GAS_MAP_PATH,
  OIL_AND_GAS_MAP_PATH_WITH_LATLONG,
  OIL_AND_GAS_MAP_PATH_WITH_LATLONG_AND_ZOOM,
  PORTFOLIO_ANALYSIS_PATH,
  RESERVES_DEFINITION_PATH,
  ROOT_PATH,
  SETTINGS_PATH,
  VALUATIONS_ASSET_PATH,
  VALUATIONS_ASSET_REPORT_PATH,
  VALUATIONS_PATH,
  VALUATIONS_V2_ASSET_PATH,
  VALUATIONS_V2_PATH,
  VIDEO_TUTORIALS_PATH,
  WEA_ASSUMPTIONS_PATH,
} from '@/utils/constants';

type RouteConfig = {
  path: string;
  Page: ComponentType;
  forceNewTab?: boolean;
};

export const useProtectedRoutes = () => {
  // Extract only the flags we need; this helps stability even if the provider re-creates objects.
  const { valuationsV2 } = useFeatureFlags();

  const routes = useMemo<RouteConfig[]>(() => {
    const flagged: RouteConfig[] = [];

    if (valuationsV2) {
      flagged.push({ path: VALUATIONS_V2_PATH, Page: ValuationsV2 });
      flagged.push({ path: VALUATIONS_V2_ASSET_PATH, Page: ValuationsV2 });
    }

    return [
      { path: ROOT_PATH, Page: LandingPage },
      { path: CCUS_PATH, Page: CCUS, forceNewTab: true },
      { path: GHG_PATH, Page: GHG, forceNewTab: true },
      { path: GLOBAL_DOWNLOADS_PATH, Page: GlobalDownloadsPage },
      { path: GLOBAL_DOWNLOADS_PATH_WITH_DOWNLOADSPANE, Page: GlobalDownloadsPage },
      { path: PORTFOLIO_ANALYSIS_PATH, Page: PortfolioAnalysis, forceNewTab: true },
      { path: OIL_AND_GAS_MAP_PATH, Page: OilAndGasMap, forceNewTab: true },
      { path: OIL_AND_GAS_MAP_PATH_WITH_LATLONG, Page: OilAndGasMap },
      { path: OIL_AND_GAS_MAP_PATH_WITH_LATLONG_AND_ZOOM, Page: OilAndGasMap },
      { path: VALUATIONS_PATH, Page: Valuations, forceNewTab: true },
      { path: VALUATIONS_ASSET_PATH, Page: AssetTemplate },
      { path: VALUATIONS_ASSET_REPORT_PATH, Page: AssetTemplate },
      { path: COUNTRY_OVERVIEWS_PATH, Page: CountryOverviews },
      { path: M_A_TRANSACTIONS_PATH, Page: MaTransactionsPage },
      { path: M_A_OPPORTUNITIES_PATH, Page: MaOpportunitiesPage },
      { path: CUSTOMER_DOWNLOADS_PATH, Page: SubscriberDownload },
      { path: RESERVES_DEFINITION_PATH, Page: GuidanceDocumentation },
      { path: WEA_ASSUMPTIONS_PATH, Page: GuidanceDocumentation },
      { path: SETTINGS_PATH, Page: Settings },
      { path: VIDEO_TUTORIALS_PATH, Page: VideoTutorials },
      ...flagged,
      // DO NOT change after this line: DEFAULT_PATH must be last as a catch-all.
      { path: DEFAULT_PATH, Page: LandingPage },
    ];
  }, [valuationsV2]);

  return routes;
};
